import { StateCreator } from 'zustand'
import { logger, enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { ShiftType } from '$constants'
import { IClockinHistory, IClockinResponse, ReportsMetadata } from '$models'
import { SharedSlice } from 'src/components/slice'
import { TimeclockSharedSlice } from '../slice'
import { timeClockinService } from '$services'

interface StaffClocksState {
  showDeactivated: boolean
}

export interface StaffClocksSlice extends StaffClocksState {
  setShowDeactivatedClocks: () => void

  /**
   * Resets StaffClocks Slice state
   */
  resetStaffClocksSlice: () => void

  /**
   * Get IClockinResponse history for the clockinId provided
   * @param clockinId IClockinResponse identifier
   * @returns Promise<ClockinHistory[]>
   */
  getClockinDetailHistory: (clockinId: string) => Promise<IClockinHistory[]>

  /**
   * Add new IClockinResponse
   * @param newClockin IClockinResponse to add
   * @param reportsMetadata Reports metadata (date and date range filters)
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   */
  addClockinDetail: (
    newClock: IClockinResponse,
    reportsMetadata: ReportsMetadata,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>
}

const initialState: StaffClocksState = {
  showDeactivated: false,
}

const createStaffClocksSlice: StateCreator<
  StaffClocksSlice & TimeclockSharedSlice & SharedSlice,
  [],
  [],
  StaffClocksSlice
> = (set, get) => ({
  // State variables
  ...initialState,

  // State setters
  setShowDeactivatedClocks: () =>
    set((state) => ({ showDeactivated: !state.showDeactivated })),
  resetStaffClocksSlice: () => set(initialState),

  // State API calls
  addClockinDetail: async (
    newClockin: IClockinResponse,
    reportsMetadata: ReportsMetadata,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      // TAKEN FROM ANGULAR:
      // Small hack for getting around the problem where changing the hourlyRate updates
      // the newClock's hourlyRate from undefined to null.
      // the latter will trigger a validation error
      // See LOADED-1226
      if (newClockin.hourlyRate == null) {
        delete newClockin.hourlyRate
      }

      const newClockinDetail: IClockinResponse = await timeClockinService.add(
        newClockin
      )

      if (!newClockinDetail) {
        logger.error(`${errorMessage} - newClockinDetail doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      // If the clockin time is not between the current date range then it is not added to the current clockins list
      // but the clockin has been added successfully and that is why the snackbar message
      if (
        newClockinDetail.clockinTime.isBetween(
          reportsMetadata.startDateTime,
          reportsMetadata.endDateTime,
          'day',
          '[]'
        )
      ) {
        // We need to fetch the clockins again to update the list, since the new clockin can affect the Rate
        // of other entries. This happens when the clockin belongs to a staff member who had a Salary type
        // remuneration timeline during that period
        await get().getTimeClockins(
          reportsMetadata.startDateTime,
          reportsMetadata.endDateTime,
          true,
          [ShiftType.Clockin],
          undefined,
          false
        )

        enqueueLoadedSnackbar(successMessage, 'success')
        return
      }

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  getClockinDetailHistory: async (
    clockinId: string
  ): Promise<IClockinHistory[]> => {
    return timeClockinService
      .getClockinHistory(
        undefined,
        undefined,
        undefined,
        clockinId,
        1,
        100, // Arbitrary large number to get all the history items for this clockin
        undefined
      )
      .catch((error) => {
        exceptionMessageHandler(error, 'Failed to retrieve clickin history')
        return []
      })
  },
})

export default createStaffClocksSlice
